/**
 * Admin Strategy Parameters Page — Plan B+ Faz 3 / Parça 2 (2026-02-13 v26)
 *
 * 6 ENTRY trigger stratejisi + 6 sistem parametresi için runtime override paneli.
 * Backend: /api/admin/strategy-parameters/...
 */
import { Activity, History, RotateCcw, Save, ShieldAlert, Sliders, Trash2, Loader2, AlertTriangle, Beaker } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import * as React from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { apiClient } from "@/lib/api";

const STRATEGY_LABELS = {
  darvas_volume_breakout: "Darvas Volume Breakout",
  fisher_macddema: "Fisher + MACD/DEMA",
  hhv_llv_breakout: "HHV/LLV Breakout",
  bollinger_squeeze_breakout: "Bollinger Squeeze Breakout",
  supertrend_flip: "SuperTrend Flip",
  tsi_momentum: "TSI Momentum",
  symbol_repeat_loss_blackout_v1: "Symbol Repeat-Loss Blackout — V1 (Her Zaman Aktif)",
  symbol_repeat_loss_blackout: "Symbol Repeat-Loss Blackout — V2 (Mode: SHADOW/ENFORCE)",
  manual_symbol_blacklist: "Manual Symbol Blacklist — ENV Statik Liste (Mode: SHADOW/ENFORCE)",
  _system: "Sistem Guard'ları (Plan B+)",
};

// 2026-07-16 GÖREV EMRİ — V1/V2 karışıklığını önlemek + DB-blacklist'in ayrı ve
// her zaman aktif olduğunu netleştirmek için kart üstü bilgi notları.
const CARD_NOTES = {
  symbol_repeat_loss_blackout_v1: "HER ZAMAN AKTİF — bu sistemde \"mode\" kavramı YOK; koşulsuz enforcement yapar (dispatch anında early-block). Aşağıdaki \"V2 (Mode: SHADOW/ENFORCE)\" kartıyla KARIŞTIRMAYIN — V2 şu an sadece SHADOW modda log üretir, trade akışını etkilemez.",
  symbol_repeat_loss_blackout: "Bu V2 kartı şu an SADECE gözlemsel (mode=SHADOW → sadece log, trade'i etkilemez). Gerçek koşulsuz enforcement yukarıdaki \"V1 (Her Zaman Aktif)\" kartındadır.",
  manual_symbol_blacklist: "Bu liste (ENV tabanlı statik CSV, mode SHADOW/ENFORCE) admin panelindeki DB-tabanlı \"Manuel Sembol Ban\" kayıtlarından (POST /api/admin/symbol-blacklist ile eklenen, user bazlı süreli ban) TAMAMEN AYRIDIR. DB-tabanlı ban listesi mode kontrolü olmadan HER ZAMAN AKTİF çalışır — bu ayar SADECE aşağıdaki ENV statik CSV listesini etkiler.",
};

export const AdminStrategyParametersPage = () => {
  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState({});
  const [overrides, setOverrides] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [drafts, setDrafts] = useState({}); // { "sid::key": value }
  const [busy, setBusy] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const [catRes, ovRes, audRes] = await Promise.all([
        apiClient.get("/admin/strategy-parameters/catalog"),
        apiClient.get("/admin/strategy-parameters/overrides"),
        apiClient.get("/admin/strategy-parameters/audit?limit=50"),
      ]);
      setCatalog(catRes.data?.catalog || {});
      setOverrides(ovRes.data?.overrides || []);
      setAuditLog(audRes.data?.audit || []);
      // drafts: initialize with override values or defaults
      const init = {};
      const ovMap = new Map();
      (ovRes.data?.overrides || []).forEach((o) => {
        ovMap.set(`${o.strategy_id}::${o.parameter_key}`, o.parameter_value);
      });
      Object.entries(catRes.data?.catalog || {}).forEach(([sid, params]) => {
        params.forEach((p) => {
          const k = `${sid}::${p.key}`;
          init[k] = ovMap.has(k) ? ovMap.get(k) : p.default;
        });
      });
      setDrafts(init);
    } catch (err) {
      toast.error("Strategy parametreleri alınamadı");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const overrideMap = useMemo(() => {
    const m = new Map();
    overrides.forEach((o) => m.set(`${o.strategy_id}::${o.parameter_key}`, o));
    return m;
  }, [overrides]);

  const applyParameter = async (sid, param, environment = "both", timeframe = "") => {
    const key = timeframe ? `${sid}::${param.key}::${timeframe}` : `${sid}::${param.key}`;
    const value = drafts[key];
    if (value === undefined || value === null || value === "") {
      toast.error("Değer boş olamaz");
      return;
    }
    setBusy(key);
    try {
      // 2026-07-15 Ek Madde: timeframe query param — TF-özel override.
      const qp = timeframe ? `?timeframe=${encodeURIComponent(timeframe)}` : "";
      await apiClient.put(
        `/admin/strategy-parameters/overrides/${encodeURIComponent(sid)}/${encodeURIComponent(param.key)}${qp}`,
        {
          value: param.type === "bool" ? Boolean(value) : (param.type === "int" ? Math.round(Number(value)) : Number(value)),
          parameter_type: param.type,
          environment,
          reason: `Admin UI update: ${param.key}${timeframe ? `[${timeframe}]` : ""} → ${value} (env=${environment})`,
        }
      );
      toast.success(`${STRATEGY_LABELS[sid] || sid} / ${param.key}${timeframe ? ` [${timeframe}]` : ""} güncellendi`);
      await reload();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Güncelleme başarısız";
      toast.error(msg);
    } finally {
      setBusy(null);
    }
  };

  const resetParameter = async (sid, param, timeframe = "") => {
    const key = timeframe ? `${sid}::${param.key}::${timeframe}` : `${sid}::${param.key}`;
    setBusy(key);
    try {
      const qp = timeframe ? `&timeframe=${encodeURIComponent(timeframe)}` : "";
      await apiClient.delete(
        `/admin/strategy-parameters/overrides/${encodeURIComponent(sid)}/${encodeURIComponent(param.key)}?environment=both&reason=Admin+UI+reset${qp}`
      );
      toast.success(`${param.key}${timeframe ? ` [${timeframe}]` : ""} default'a döndürüldü`);
      await reload();
    } catch (err) {
      toast.error("Reset başarısız");
    } finally {
      setBusy(null);
    }
  };

  const invalidateCache = async () => {
    try {
      await apiClient.post("/admin/strategy-parameters/cache/invalidate");
      toast.success("Cache temizlendi — değişiklikler 1 sn içinde etkin");
      await reload();
    } catch (err) {
      toast.error("Cache temizleme başarısız");
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-4" data-testid="admin-strategy-params-page">
        <LoadingSkeleton lines={6} />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="admin-strategy-params-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-slate-100 flex items-center gap-2" data-testid="page-title">
            <Sliders className="h-7 w-7 text-cyan-400" /> Strategy Parameter Admin
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Plan B+ Faz 3 / Parça 2 — Runtime threshold ayarlama (DB-öncelikli, 30s TTL cache).
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={invalidateCache} data-testid="invalidate-cache-btn">
            <RotateCcw className="h-4 w-4 mr-1" /> Cache Invalidate
          </Button>
          <Button variant="outline" size="sm" onClick={reload} data-testid="reload-btn">
            Reload
          </Button>
        </div>
      </div>

      <Tabs defaultValue="strategies" className="space-y-4">
        <TabsList data-testid="tabs-list">
          <TabsTrigger value="strategies" data-testid="tab-strategies">Strategies</TabsTrigger>
          <TabsTrigger value="system" data-testid="tab-system">Sistem Guard&apos;ları</TabsTrigger>
          <TabsTrigger value="audit-preview" data-testid="tab-audit-preview">
            <Beaker className="h-4 w-4 mr-1" /> Audit &amp; Preview
          </TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">
            <History className="h-4 w-4 mr-1" /> Audit Log ({auditLog.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="strategies" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(catalog)
              .filter(([sid]) => sid !== "_system")
              .map(([sid, params]) => (
                <StrategyCard
                  key={sid}
                  sid={sid}
                  params={params}
                  drafts={drafts}
                  setDrafts={setDrafts}
                  overrideMap={overrideMap}
                  applyParameter={applyParameter}
                  resetParameter={resetParameter}
                  busy={busy}
                />
              ))}
          </div>
        </TabsContent>

        <TabsContent value="system" className="space-y-4">
          {catalog._system && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <StrategyCard
                sid="_system"
                params={catalog._system}
                drafts={drafts}
                setDrafts={setDrafts}
                overrideMap={overrideMap}
                applyParameter={applyParameter}
                resetParameter={resetParameter}
                busy={busy}
              />
            </div>
          )}
        </TabsContent>

        <TabsContent value="audit-preview" className="space-y-4">
          <AuditPreviewPanel
            catalog={catalog}
            overrideMap={overrideMap}
            applyParameter={applyParameter}
            resetParameter={resetParameter}
            drafts={drafts}
            setDrafts={setDrafts}
            busy={busy}
          />
        </TabsContent>

        <TabsContent value="audit" className="space-y-2">
          {auditLog.length === 0 ? (
            <p className="text-sm text-slate-400">Henüz audit kaydı yok.</p>
          ) : (
            <div className="space-y-2" data-testid="audit-log-list">
              {auditLog.map((a) => (
                <div
                  key={a.id}
                  className="bg-slate-800/60 border border-slate-700/40 rounded-lg p-3 text-sm"
                  data-testid={`audit-entry-${a.id}`}
                >
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <Badge variant={a.action === "delete" || a.action === "reset" ? "destructive" : "default"}>
                        {a.action}
                      </Badge>
                      <span className="font-mono text-cyan-300">
                        {a.strategy_id} / {a.parameter_key}
                      </span>
                    </div>
                    <span className="text-xs text-slate-500">
                      {a.created_at ? new Date(a.created_at).toLocaleString("tr-TR") : "-"}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-slate-300 grid grid-cols-2 gap-2">
                    <span>Eski: <code>{JSON.stringify(a.old_value)}</code></span>
                    <span>Yeni: <code>{JSON.stringify(a.new_value)}</code></span>
                  </div>
                  {a.reason && <p className="mt-1 text-xs text-slate-400 italic">{a.reason}</p>}
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

const StrategyCard = ({ sid, params, drafts, setDrafts, overrideMap, applyParameter, resetParameter, busy }) => {
  // 2026-07-15 Ek Madde: her parametre için timeframe seçimi (default "all" = agnostic).
  const [timeframeBySlot, setTimeframeBySlot] = React.useState({});
  const _getTf = (k) => timeframeBySlot[k] || "";
  const _setTf = (k, v) => setTimeframeBySlot((prev) => ({ ...prev, [k]: v }));

  return (
    <div
      className="bg-slate-900/60 border border-slate-700/50 rounded-xl p-5 space-y-4"
      data-testid={`strategy-card-${sid}`}
    >
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-100">{STRATEGY_LABELS[sid] || sid}</h2>
        <span className="text-xs font-mono text-slate-500">{sid}</span>
      </div>
      {CARD_NOTES[sid] && (
        <div
          className="rounded-lg border border-amber-800/50 bg-amber-950/30 p-2.5 text-[11px] text-amber-200 flex items-start gap-2"
          data-testid={`card-note-${sid}`}
        >
          <ShieldAlert className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>{CARD_NOTES[sid]}</span>
        </div>
      )}
      <div className="space-y-3">
        {params.map((p) => {
          const _tfSel = _getTf(p.key);
          const _dbKey = _tfSel ? `${sid}::${p.key}__${_tfSel}` : `${sid}::${p.key}`;
          const k = _tfSel ? `${sid}::${p.key}::${_tfSel}` : `${sid}::${p.key}`;
          const draft = drafts[k];
          const override = overrideMap.get(_dbKey);
          const isOverridden = !!override;
          const isBusy = busy === k;
          const _tfOptions = Array.isArray(p.timeframes) ? p.timeframes : [];
          return (
            <div
              key={p.key}
              className="space-y-1.5 pb-3 border-b border-slate-700/30 last:border-0"
              data-testid={`param-row-${sid}-${p.key}`}
            >
              <div className="flex items-center justify-between gap-2">
                <Label className="text-sm text-slate-200 flex items-center gap-2">
                  {p.key}
                  {_tfSel && (
                    <Badge variant="outline" className="text-[10px] border-purple-500/50 text-purple-300">
                      TF: {_tfSel}
                    </Badge>
                  )}
                  {isOverridden && (
                    <Badge variant="secondary" className="text-[10px] bg-cyan-900/60 text-cyan-300 border-cyan-800">
                      override
                    </Badge>
                  )}
                </Label>
                <span className="text-[11px] text-slate-500">
                  default: <span className="font-mono">{String(p.default)}</span>
                  {p.min !== undefined && p.min !== null && p.max !== undefined && p.max !== null && (
                    <> · range: {p.min}–{p.max}</>
                  )}
                </span>
              </div>
              {p.description && <p className="text-[11px] text-slate-500">{p.description}</p>}
              {_tfOptions.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-slate-500">Timeframe:</span>
                  <select
                    value={_tfSel}
                    onChange={(e) => _setTf(p.key, e.target.value)}
                    className="h-7 text-xs bg-slate-800/80 text-slate-100 border border-slate-700/40 rounded px-2"
                    data-testid={`param-timeframe-select-${sid}-${p.key}`}
                  >
                    <option value="">agnostic (varsayılan)</option>
                    {_tfOptions.map((tf) => (
                      <option key={tf} value={tf}>{tf}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex items-center gap-2">
                {p.type === "bool" ? (
                  <Switch
                    checked={Boolean(draft)}
                    onCheckedChange={(v) => setDrafts((prev) => ({ ...prev, [k]: v }))}
                    data-testid={`param-switch-${sid}-${p.key}`}
                  />
                ) : (
                  <Input
                    type="number"
                    value={draft ?? ""}
                    step={p.type === "int" ? 1 : 0.001}
                    min={p.min ?? undefined}
                    max={p.max ?? undefined}
                    onChange={(e) => setDrafts((prev) => ({ ...prev, [k]: e.target.value }))}
                    className="h-8 text-sm font-mono bg-slate-800/80"
                    data-testid={`param-input-${sid}-${p.key}`}
                  />
                )}
                <Button
                  size="sm"
                  variant="default"
                  onClick={() => applyParameter(sid, p, "both", _tfSel)}
                  disabled={isBusy}
                  data-testid={`param-apply-${sid}-${p.key}`}
                >
                  {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                  <span className="ml-1">Apply</span>
                </Button>
                {isOverridden && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => resetParameter(sid, p, _tfSel)}
                    disabled={isBusy}
                    data-testid={`param-reset-${sid}-${p.key}`}
                  >
                    <Trash2 className="h-3 w-3 mr-1 text-red-400" /> Reset
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="text-[10px] text-slate-500 flex items-center gap-1">
        <ShieldAlert className="h-3 w-3" /> Değişiklikler 30 saniye TTL cache sonrası otomatik etkin.
      </div>
    </div>
  );
};

export default AdminStrategyParametersPage;

// ---------------------------------------------------------------------------
// Plan B+ Faz 3 / Parça 3 — Audit & Preview Tab
// ---------------------------------------------------------------------------

const NUMERIC_TYPES = new Set(["float", "int"]);

const AuditPreviewPanel = ({ catalog, overrideMap, applyParameter, resetParameter, drafts, setDrafts, busy }) => {
  const [windowDays, setWindowDays] = useState(7);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [previews, setPreviews] = useState({}); // key "sid::pk" → preview response
  const [previewBusy, setPreviewBusy] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null); // {sid, param, value, environment}

  const reloadSummary = useCallback(async (days = windowDays) => {
    setSummaryLoading(true);
    try {
      const res = await apiClient.get(`/admin/strategy-parameters/near-miss/summary?window_days=${days}`);
      setSummary(res.data);
    } catch (err) {
      toast.error("Near-miss özeti alınamadı");
    } finally {
      setSummaryLoading(false);
    }
  }, [windowDays]);

  useEffect(() => {
    reloadSummary(windowDays);
  }, [windowDays, reloadSummary]);

  const runPreview = async (sid, param, candidate) => {
    const key = `${sid}::${param.key}`;
    setPreviewBusy(key);
    try {
      const res = await apiClient.post("/admin/strategy-parameters/backtest-preview", {
        strategy_id: sid,
        parameter_key: param.key,
        candidate_value: param.type === "int" ? Math.round(Number(candidate)) : Number(candidate),
        window_days: windowDays,
      });
      setPreviews((prev) => ({ ...prev, [key]: res.data }));
      toast.success(`${param.key} için preview hazır`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Backtest preview başarısız";
      toast.error(msg);
      setPreviews((prev) => ({ ...prev, [key]: { error: msg } }));
    } finally {
      setPreviewBusy(null);
    }
  };

  const requestApply = (sid, param, value) => {
    setConfirmModal({ sid, param, value, environment: "testnet" });
  };

  const confirmApply = async () => {
    if (!confirmModal) return;
    const { sid, param, value, environment } = confirmModal;
    // Mainnet için ek 2. confirm zorunlu — modal'ı kapatma, "confirmed: true" flag set et.
    if (environment === "prod" && !confirmModal.confirmed) {
      setConfirmModal({ ...confirmModal, confirmed: true });
      return;
    }
    setConfirmModal(null);
    // Patch drafts so the existing applyParameter picks up the candidate value.
    setDrafts((prev) => ({ ...prev, [`${sid}::${param.key}`]: value }));
    // Slight delay so React state propagates before applyParameter reads drafts.
    await new Promise((r) => setTimeout(r, 50));
    await applyParameter(sid, param, environment);
  };

  const numericStrategies = useMemo(() => {
    return Object.entries(catalog || {})
      .filter(([sid]) => sid !== "_system")
      .map(([sid, params]) => [
        sid,
        (params || []).filter((p) => NUMERIC_TYPES.has(p.type)),
      ]);
  }, [catalog]);

  return (
    <div className="space-y-5" data-testid="audit-preview-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-amber-400" />
          <span className="text-sm text-slate-300">
            Near-miss penceresi (gün):
          </span>
          <Input
            type="number"
            min={1}
            max={30}
            value={windowDays}
            onChange={(e) => setWindowDays(Math.max(1, Math.min(30, Number(e.target.value) || 7)))}
            className="h-8 w-20 text-sm font-mono bg-slate-800/80"
            data-testid="audit-window-days-input"
          />
          <Button size="sm" variant="outline" onClick={() => reloadSummary(windowDays)} data-testid="audit-reload-summary-btn">
            {summaryLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : "Yenile"}
          </Button>
        </div>
        {summary && (
          <Badge variant="secondary" className="bg-amber-900/50 text-amber-200 border-amber-700/50" data-testid="audit-total-events">
            Toplam near-miss event: {summary.total}
          </Badge>
        )}
      </div>

      <div className="rounded-lg border border-amber-900/40 bg-amber-950/30 p-3 text-xs text-amber-200 flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          <strong>PnL projeksiyonu bu fazda YAPILMAZ.</strong> Önizleme yalnızca son {windowDays} günde aday değerle kaç ek sinyal oluşurdu sayısını gösterir.
          historical_avg_r yalnızca bilgilendirme amaçlıdır. Trade karar mantığı değişmez.
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {numericStrategies.map(([sid, params]) => {
          if (!params.length) return null;
          return (
            <div
              key={sid}
              className="bg-slate-900/60 border border-slate-700/50 rounded-xl p-4 space-y-3"
              data-testid={`audit-card-${sid}`}
            >
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-slate-100">
                  {STRATEGY_LABELS[sid] || sid}
                </h3>
                <span className="text-[10px] font-mono text-slate-500">{sid}</span>
              </div>
              {params.map((p) => {
                const k = `${sid}::${p.key}`;
                const override = overrideMap.get(k);
                const currentValue = override
                  ? (override.parameter_value)
                  : p.default;
                const candidate = drafts[k] ?? currentValue;
                const preview = previews[k];
                const nmRow = summary?.by_param?.find((r) => r.strategy_id === sid && r.parameter_key === p.key);
                const nmCount = nmRow ? nmRow.count : 0;
                const isBusy = previewBusy === k || busy === k;
                return (
                  <div
                    key={p.key}
                    className="rounded-md border border-slate-700/40 bg-slate-950/40 p-3 space-y-2"
                    data-testid={`audit-row-${sid}-${p.key}`}
                  >
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <Label className="text-sm font-mono text-cyan-300">
                        {p.key}
                      </Label>
                      <div className="flex items-center gap-2 text-[10px] text-slate-400">
                        <span>current: <span className="font-mono text-slate-200">{String(currentValue)}</span></span>
                        <span>·</span>
                        <span>default: <span className="font-mono">{String(p.default)}</span></span>
                        {p.min !== null && p.min !== undefined && p.max !== null && p.max !== undefined && (
                          <>
                            <span>·</span>
                            <span>safe: {p.min}–{p.max}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Input
                        type="number"
                        value={candidate ?? ""}
                        step={p.type === "int" ? 1 : 0.001}
                        min={p.min ?? undefined}
                        max={p.max ?? undefined}
                        onChange={(e) => setDrafts((prev) => ({ ...prev, [k]: e.target.value }))}
                        className="h-8 text-sm font-mono bg-slate-800/80 flex-1 min-w-[100px]"
                        data-testid={`audit-input-${sid}-${p.key}`}
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={isBusy}
                        onClick={() => runPreview(sid, p, candidate)}
                        data-testid={`audit-preview-${sid}-${p.key}`}
                      >
                        {previewBusy === k ? <Loader2 className="h-3 w-3 animate-spin" /> : <Beaker className="h-3 w-3" />}
                        <span className="ml-1">Preview</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="default"
                        disabled={isBusy}
                        onClick={() => requestApply(sid, p, candidate)}
                        data-testid={`audit-apply-${sid}-${p.key}`}
                      >
                        <Save className="h-3 w-3 mr-1" /> Apply
                      </Button>
                      {override && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={isBusy}
                          onClick={() => resetParameter(sid, p)}
                          data-testid={`audit-reset-${sid}-${p.key}`}
                        >
                          <Trash2 className="h-3 w-3 mr-1 text-red-400" /> Reset
                        </Button>
                      )}
                    </div>
                    <div className="flex items-center gap-3 flex-wrap text-[11px]">
                      <Badge variant="secondary" className="bg-slate-800 text-slate-300 border-slate-700/60" data-testid={`audit-nm-count-${sid}-${p.key}`}>
                        near-miss: {nmCount}
                      </Badge>
                      {preview && !preview.error && (
                        <>
                          <Badge variant="secondary" className="bg-emerald-900/40 text-emerald-200 border-emerald-700/50" data-testid={`audit-preview-additional-${sid}-${p.key}`}>
                            +{preview.additional_signal_count} ek sinyal
                          </Badge>
                          <Badge
                            variant="secondary"
                            className={
                              preview.confidence_score === "high"
                                ? "bg-cyan-900/40 text-cyan-200 border-cyan-700/50"
                                : preview.confidence_score === "medium"
                                  ? "bg-amber-900/40 text-amber-200 border-amber-700/50"
                                  : "bg-rose-900/40 text-rose-200 border-rose-700/50"
                            }
                            data-testid={`audit-preview-confidence-${sid}-${p.key}`}
                          >
                            confidence: {preview.confidence_score}
                          </Badge>
                          {preview.historical_avg_r !== null && preview.historical_avg_r !== undefined && (
                            <Badge variant="secondary" className="bg-slate-800 text-slate-300 border-slate-700/60">
                              avg R (info): {preview.historical_avg_r} (n={preview.historical_sample_size})
                            </Badge>
                          )}
                        </>
                      )}
                    </div>
                    {preview && preview.caution_note && (
                      <p className="text-[11px] italic text-amber-300/80 flex items-start gap-1">
                        <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" /> {preview.caution_note}
                      </p>
                    )}
                    {preview && preview.error && (
                      <p className="text-[11px] text-rose-400">{preview.error}</p>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      <Dialog open={!!confirmModal} onOpenChange={(v) => { if (!v) setConfirmModal(null); }}>
        <DialogContent data-testid="audit-confirm-modal" className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-400" /> Apply Override
            </DialogTitle>
            <DialogDescription>
              Ortam seçimi zorunlu. Mainnet seçilirse 2. onay istenecektir.
            </DialogDescription>
          </DialogHeader>
          {confirmModal && (
            <div className="space-y-3 text-sm">
              <div className="rounded bg-slate-800/60 p-3 font-mono text-xs space-y-1">
                <div>strategy: <span className="text-cyan-300">{confirmModal.sid}</span></div>
                <div>parameter: <span className="text-cyan-300">{confirmModal.param.key}</span></div>
                <div>new value: <span className="text-emerald-300">{String(confirmModal.value)}</span></div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant={confirmModal.environment === "testnet" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setConfirmModal({ ...confirmModal, environment: "testnet", confirmed: false })}
                  data-testid="audit-env-testnet-btn"
                >
                  Testnet
                </Button>
                <Button
                  variant={confirmModal.environment === "prod" ? "destructive" : "outline"}
                  size="sm"
                  onClick={() => setConfirmModal({ ...confirmModal, environment: "prod", confirmed: false })}
                  data-testid="audit-env-mainnet-btn"
                >
                  Mainnet
                </Button>
              </div>
              {confirmModal.environment === "prod" && (
                <div className="rounded border border-rose-700/60 bg-rose-950/40 p-2 text-xs text-rose-200 flex items-start gap-2" data-testid="audit-mainnet-warning">
                  <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                  <span>
                    <strong>Bu değişiklik canlı sistemi etkileyecek.</strong>{" "}
                    {confirmModal.confirmed
                      ? "Onaylamak için bir kez daha 'Confirm Apply' butonuna basın."
                      : "Devam etmek için 'Confirm Apply' butonuna basın — ek bir onay daha istenecek."}
                  </span>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmModal(null)} data-testid="audit-confirm-cancel">
              İptal
            </Button>
            <Button
              variant={confirmModal?.environment === "prod" ? "destructive" : "default"}
              onClick={confirmApply}
              data-testid="audit-confirm-apply"
            >
              <Save className="h-3 w-3 mr-1" />
              {confirmModal?.environment === "prod" && !confirmModal?.confirmed
                ? "Confirm Apply (Mainnet)"
                : confirmModal?.environment === "prod"
                  ? "FINAL Confirm — Apply to Mainnet"
                  : "Confirm Apply (Testnet)"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

